import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";
import Divider from "@mui/joy/Divider";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import React from "react";
import Alert from "@mui/joy/Alert";
import Stack from "@mui/joy/Stack";
import { StudyId } from "../../../models/study";
import { StudyQuartzClientContext } from "../../../shared/services/api/contexts";
import { DrawerWrapper } from "../../../shared/components/layout/DrawerWrapper";

interface StudyParticipantCSVDrawerProps {
  readonly studyId: StudyId;
  readonly onClose: () => void;
  readonly onCSVImported: () => void;
}

export default function StudyParticipantCSVDrawer({
  studyId,
  onClose,
  onCSVImported,
}: StudyParticipantCSVDrawerProps) {
  const studyQuartzClient = React.useContext(StudyQuartzClientContext);
  const [processing, setProcessing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate that the file is CSV and is less than or equal to 1MB
      if (!file.name.endsWith(".csv")) {
        setError("Only CSV files are allowed.");
        return;
      }
      if (file.size > 1 * 1024 * 1024) {
        setError("File size exceeds the maximum limit of 1MB.");
        return;
      }

      // Reset any previous errors and start processing
      setError(null);
      setProcessing(true);
      try {
        await studyQuartzClient.importParticipantsCSV(studyId, file);
        onCSVImported();
      } catch (err: any) {
        const errorMessage = err?.message || "An error occurred while processing the file.";
        setError(errorMessage);
      } finally {
        setProcessing(false);
      }
    }
  };

  return (
    <DrawerWrapper>
      <>
        <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
          <Typography level="title-md" sx={{ flex: 1 }}>
            Import multiple participants from CSV
          </Typography>
          <IconButton component="span" variant="plain" color="neutral" size="sm">
            <CloseRoundedIcon onClick={onClose} />
          </IconButton>
        </Box>
        <Divider />
        {processing ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ background: "white", flexGrow: 1, p: 2 }}>
            <Stack sx={{ mb: 2 }} direction="column" gap={2}>
              <Alert variant="outlined">
                Import participants in bulk with a preselected list of patient IDs. The CSV file can
                have two columns:
                <br />
                <br />
                - IPP (compulsory)
                <br />
                - ID (optional): define the ID of the participant within the project
                <br />
                <br />
                All patients found within the database will be automatically linked to the project
                with the status "screening". The CSV file must be less than or equal to 1MB.
              </Alert>
              <Button component="label" variant="outlined">
                Select CSV File{" "}
                <input type="file" accept=".csv" hidden onChange={handleFileChange} />
              </Button>
              {error && (
                <Typography color="danger" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
            </Stack>
          </Box>
        )}
      </>
    </DrawerWrapper>
  );
}
